import React, { useState } from "react";
import CommonAccountPanelView from "presentational/account/CommonAccountPanelView";
import LoadingModalView from "presentational/common/modals/LoadingModalView";
import TXT from "presentational/common/multinational/TXT";
import { withStore } from "store/storeHandler";
import { checkEmailExist } from "lib/accountDuplicationChecker";
import { isValidInput } from "lib/textFilter";
import Service from "lib/service-amp";
import Restful from "lib/restful";
import PropTypes from "prop-types";

LogInPanel.propTypes = {
  onModeToggleBtnClick: PropTypes.func.isRequired,
  goNextUrl: PropTypes.func.isRequired,
  goToSocialPage: PropTypes.func.isRequired,
  onForgotPwBtnClick: PropTypes.func.isRequired
};

function LogInPanel(props) {
  const {
    onModeToggleBtnClick,
    goNextUrl,
    goToSocialPage,
    onForgotPwBtnClick,
    MyAccountActions,
    UserProfileActions,
    CognitoAuthActions,
    AppErrorActions,
    GoogleAnalyticsActions,
    UserDeviceActions,
    NoticeModalActions
  } = props;
  const [email, setEmail] = useState("");
  const [passwordForAuth, setPasswordForAuth] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const displayNotice = (type, callback) => {
    let content;
    switch (type) {
      case "inputNotFilledOut":
        content = <TXT name="msg_enterEmailAndPassword" />;
        break;
      case "wrongEmail":
        content = <TXT name="msg_enterValidEmail" />;
        break;
      case "wrongInputData":
        content = <TXT name="msg_wrongEmailOrPassword" />;
        break;
      case "needGoogleLogin":
        content = <TXT name="msg_registeredUserInGoogle" />;
        break;
      case "activatedAccount":
        content = <TXT name="msg_accountActivated" />;
        break;
      case "defaultLogInError":
        content = <TXT name="msg_commonNetworkError" />;
        break;
      default:
        console.log("error : null type");
        return null;
    }
    NoticeModalActions.openModal({ content, callback });
  };

  const handleTextChange = (type, value) => {
    if (type === "email") {
      setEmail(value);

      // [로그인 트릭 코드] 시작
      const nameArr = [
        "krm",
        "krw",
        "usm",
        "usw",
        "gbm",
        "gbw",
        "cam",
        "caw",
        "rum",
        "ruw",
        "jpm",
        "jpw",
        "itm",
        "itw",
        "esm",
        "esw",
        "dem",
        "dew",
        "frm",
        "frw",
        "krm2",
        "krw2",
        "usm2",
        "usw2",
        "gbm2",
        "gbw2",
        "cam2",
        "caw2",
        "rum2",
        "ruw2",
        "jpm2",
        "jpw2",
        "itm2",
        "itw2",
        "esm2",
        "esw2",
        "dem2",
        "dew2",
        "frm2",
        "frw2"
      ];
      if (nameArr.includes(value)) {
        setEmail(`${value}@maunent.com`);
      }
      // [로그인 트릭 코드] 끝
    } else if (type === "passwordForAuth") {
      setPasswordForAuth(value);
    }
  };

  const activateAccount = async idToken => {
    const { code, res, errInfo } = await Restful.activateAccountStatusAPI({
      idToken
    });
    if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      AppErrorActions.setSafeAppError(errObj);
      return false;
    }
    const { old } = res;
    if (old && old === "deleted") {
      displayNotice("activatedAccount");
    }
    return true;
  };

  const logInProcess = async idToken => {
    let userProfile;
    try {
      userProfile = await UserProfileActions.initMyProfile(idToken, true);
      const { success, location } = userProfile;
      if (!success) {
        const errObj = Error("login error");
        errObj.errorValue = { success, email };
        AppErrorActions.setSafeAppError(errObj);
        return false;
      }
      await UserDeviceActions.editViewerLocation(location);
    } catch (error) {
      AppErrorActions.setSafeAppError(error);
      return false;
    }

    if (!userProfile) {
      const errObj = Error("null user profile");
      AppErrorActions.setSafeAppError(errObj);
      return false;
    }

    MyAccountActions.setLogIn({
      email: userProfile.email,
      username: userProfile.username,
      idToken
    });
    CognitoAuthActions.setAuthData({
      isEmailAuthUser: userProfile.isEmailAuthUser,
      isGoogleAuthUser: userProfile.isGoogleAuthUser
    });
    GoogleAnalyticsActions.setUserInfoForGA({
      gaUserId: userProfile.gaUserId,
      location: userProfile.location,
      gender: userProfile.gender,
      birthday: userProfile.birthday
    });
    GoogleAnalyticsActions.startAnalyzingNewUser();
    return true;
  };

  const handleCognitoLogInClick = async () => {
    if (!email || !passwordForAuth) {
      displayNotice("inputNotFilledOut");
      return null;
    }

    if (!isValidInput("email", email)) {
      displayNotice("wrongEmail");
      return null;
    }

    if (isLoading) return null;

    await setIsLoading(true);

    const { emailExist, isGoogleAuthUser, isEmailAuthUser } =
      await checkEmailExist(email.trim());
    if (!emailExist) {
      displayNotice("wrongInputData");
      setIsLoading(false);
      return null;
    } else if (!isEmailAuthUser && isGoogleAuthUser) {
      displayNotice("needGoogleLogin", () => {
        goToSocialPage();
      });
      setIsLoading(false);
      return null;
    }

    const { code, res } = await Service.logIn(
      email.trim(),
      passwordForAuth.trim()
    );
    if (code === "error") {
      displayNotice("defaultLogInError");
      AppErrorActions.setSafeAppError(Error(res));
      setIsLoading(false);
      return null;
    } else if (code === "NotAuthorizedException") {
      displayNotice("wrongInputData");
      setIsLoading(false);
      return null;
    }

    const tokenObj = res;
    CognitoAuthActions.setToken({ tokenObj });
    const idToken = tokenObj.idToken.getJwtToken();
    const [success1, success2] = await Promise.all([
      logInProcess(idToken),
      activateAccount(idToken)
    ]);
    if (success1 && success2) {
      goNextUrl();
    } else {
      displayNotice("defaultLogInError");
      setIsLoading(false);
      Service.logOut();
      CognitoAuthActions.deleteAllData();
    }
  };

  return (
    <div>
      <CommonAccountPanelView
        title={<TXT name="titlePanel_logIn" />}
        textElement={["email", "passwordForAuth"]}
        onInputValueChange={handleTextChange}
        firstBtnText={<TXT name="btnPanel_cognitoLogIn" />}
        onFirstBtnClick={handleCognitoLogInClick}
        forgetPwBtnText={<TXT name="btnForgotPassword" />}
        onForgetPwBtnClick={onForgotPwBtnClick}
        modeToggleBtnText={<TXT name="btnPanelToggle_goSignUp" />}
        onModeToggleBtnClick={onModeToggleBtnClick}
      />
      <LoadingModalView open={isLoading} />
    </div>
  );
}

export default withStore({
  dispatchMap: [
    "myAccount",
    "userProfile",
    "cognitoAuth",
    "appError",
    "googleAnalytics",
    "userDevice",
    "noticeModal"
  ]
})(LogInPanel);
